import { CheckCircle2, Circle } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatDateTimeInTz } from '../../../utils/timezone';

/**
 * A quarantine treatment's customer orders and whether each is signed off as
 * treated. Finishing the spray signs off every order still on the load, as
 * treated by the operator who finished it (Rob, 2026-10-19); someone else then
 * validates. Nothing here is pressed. Shown on the spray's page and on the
 * load's Quarantine card.
 */

export interface SignoffRow {
  order_id: number;
  order_number: string;
  customer_name: string | null;
  plants?: number;
  /** Taken off the load or cancelled since: nothing to sign. */
  gone?: boolean;
  signed_at: string | null;
  signed_by_name: string | null;
  /** Signed off by finishing the spray, not by hand. */
  on_completion?: boolean;
}

function status(o: SignoffRow, tz: string) {
  if (o.signed_at) {
    const when = formatDateTimeInTz(o.signed_at, tz);
    return o.on_completion
      ? `Treated, signed off when ${o.signed_by_name || 'the operator'} finished the spray ${when}`
      : `Treated, signed off by ${o.signed_by_name || 'someone'} ${when}`;
  }
  return o.gone ? 'No longer on the load: nothing to sign' : 'Signed off when the spray is finished';
}

export function QuarantineOrderSignoffs({ rows, tz }: { rows: SignoffRow[]; tz: string }) {
  return (
    <ul className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg">
      {rows.map((o) => (
        <li key={o.order_id} className="flex items-start gap-2 px-3 py-2">
          {o.signed_at
            ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" aria-label="Treated" />
            : <Circle className={cn('h-5 w-5 flex-shrink-0', o.gone ? 'text-neutral-300' : 'text-amber-500')} aria-label="Not signed off" />}
          <div className="min-w-0">
            <p className="text-sm font-medium text-neutral-900 break-words">
              {[o.order_number, o.customer_name].filter(Boolean).join(' · ')}
            </p>
            <p className="text-xs text-neutral-500 break-words">
              {[
                o.plants != null ? `${o.plants.toLocaleString()} plant${o.plants === 1 ? '' : 's'}` : null,
                status(o, tz),
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default QuarantineOrderSignoffs;
